import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function VanDerWaalsCalculator() {
  const [pressure, setPressure] = useState("");
  const [volume, setVolume] = useState("");
  const [moles, setMoles] = useState("");
  const [temperature, setTemperature] = useState("");
  const [a, setA] = useState("0.365");
  const [b, setB] = useState("0.0427");
  const [result, setResult] = useState<number | null>(null);

  const R = 0.08314; // L·bar/(mol·K)

  const calculatePressure = () => {
    const v = parseFloat(volume);
    const n = parseFloat(moles);
    const t = parseFloat(temperature);
    const aVal = parseFloat(a);
    const bVal = parseFloat(b);

    if (isNaN(v) || isNaN(n) || isNaN(t) || isNaN(aVal) || isNaN(bVal)) {
      toast.error("Please enter valid values");
      return;
    }

    const p = (n * R * t) / (v - n * bVal) - (aVal * n * n) / (v * v);
    setResult(p);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setPressure("");
    setVolume("");
    setMoles("");
    setTemperature("");
    setA("0.365");
    setB("0.0427");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Van der Waals Equation" calculatorPath="/calculator/van-der-waals">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>[P + a(n/V)²](V - nb) = nRT</CardTitle>
            <CardDescription>
              Improved gas law for real gases
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="volume">Volume V (L)</Label>
                <Input
                  id="volume"
                  type="number"
                  step="any"
                  placeholder="Enter volume"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="moles">Moles n (mol)</Label>
                <Input
                  id="moles"
                  type="number"
                  step="any"
                  placeholder="Enter number of moles"
                  value={moles}
                  onChange={(e) => setMoles(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature T (K)</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="any"
                  placeholder="Enter temperature"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="a">Parameter a</Label>
                  <Input
                    id="a"
                    type="number"
                    step="any"
                    placeholder="CO₂: 3.658"
                    value={a}
                    onChange={(e) => setA(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">bar·L²/mol²</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="b">Parameter b</Label>
                  <Input
                    id="b"
                    type="number"
                    step="any"
                    placeholder="CO₂: 0.04267"
                    value={b}
                    onChange={(e) => setB(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">L/mol</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculatePressure} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(3)} bar
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Pressure ({(result * 0.986923).toFixed(3)} atm)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["van-der-waals"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Van der Waals Parameters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• a accounts for intermolecular attractions</p>
                <p>• b accounts for molecular volume</p>
                <p>• CO₂: a = 3.658, b = 0.04267</p>
                <p>• N₂: a = 1.370, b = 0.0387</p>
                <p>• H₂O: a = 5.537, b = 0.03049</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
